import { Children, Fragment, isValidElement, useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import MuiTabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

// antd <Tabs> -> MUI Tabs. Supports both the v5 items API and the classic
// Tabs.TabPane children. antd auto-renders the active pane's content; MUI does
// not, so this wrapper renders it. onChange(key) (antd) vs MUI onChange(e, value).

export interface TabItem {
  key: string;
  label: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
}

interface TabPaneProps {
  tab?: ReactNode;
  key?: string;
  disabled?: boolean;
  children?: ReactNode;
}
// Metadata-only; Tabs reads its props. (antd allowed rendering it directly too.)
const TabPane = (_props: TabPaneProps): null => null;

export interface TabsProps {
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  items?: TabItem[];
  type?: "line" | "card" | "editable-card";
  tabBarExtraContent?: ReactNode;
  centered?: boolean;
  size?: "small" | "middle" | "large";
  children?: ReactNode;
  style?: CSSProperties;
  /**
   * Style for the active pane's container.
   *
   * Exists so a caller can complete a flex chain through Tabs: the content box
   * is an ordinary auto-height Box, so a pane that wants to be a bounded scroll
   * container has no ancestor to size against and simply overflows. Opt-in and
   * additive — callers that pass nothing are unaffected.
   */
  contentStyle?: CSSProperties;
  className?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}

const stripKey = (k: unknown, fallback: number) =>
  k == null ? String(fallback) : String(k).replace(/^\.\$/, "");

// antd/rc-tabs flattens <Fragment>-wrapped TabPanes (used to conditionally group
// tabs, e.g. rcg-only tabs). MUI doesn't, so a Fragment would become one bogus
// empty tab and swallow the real TabPanes inside it — recurse to extract them.
const flattenTabPanes = (children: ReactNode): ReactElement<TabPaneProps>[] =>
  Children.toArray(children).flatMap((c) => {
    if (!isValidElement(c)) return [];
    if (c.type === Fragment) return flattenTabPanes((c.props as { children?: ReactNode }).children);
    return [c as ReactElement<TabPaneProps>];
  });

export const Tabs = ({ activeKey, defaultActiveKey, onChange, items, tabBarExtraContent, centered, children, style, contentStyle, className, ...rest }: TabsProps) => {
  const tabs: TabItem[] =
    items ??
    flattenTabPanes(children).map((el, i) => ({
      key: stripKey(el.key, i),
      label: el.props.tab,
      children: el.props.children,
      disabled: el.props.disabled,
    }));

  const first = tabs[0]?.key ?? "";
  const [internal, setInternal] = useState<string>(defaultActiveKey ?? first);
  const active = activeKey ?? internal;
  const current = tabs.some((t) => t.key === active) ? active : first;

  const handleChange = (_e: unknown, val: string) => {
    if (activeKey === undefined) setInternal(val);
    onChange?.(val);
  };

  return (
    <div style={style} className={className} data-cy={(rest as Record<string, unknown>)["data-cy"] as string | undefined} data-testid={(rest as Record<string, unknown>)["data-testid"] as string | undefined}>
      <Box sx={{ display: "flex", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
        <MuiTabs value={current} onChange={handleChange} variant="scrollable" scrollButtons="auto" centered={centered} sx={{ flex: 1 }}>
          {tabs.map((t) => (
            // data-node-key preserves antd's per-tab key as a stable test hook
            // (antd rendered [data-node-key]); MUI Tab sets aria-selected on the active one.
            <Tab key={t.key} value={t.key} label={t.label} disabled={t.disabled} data-node-key={t.key} />
          ))}
        </MuiTabs>
        {tabBarExtraContent ? <Box>{tabBarExtraContent}</Box> : null}
      </Box>
      <Box sx={{ pt: 2 }} style={contentStyle}>{tabs.find((t) => t.key === current)?.children}</Box>
    </div>
  );
};

Tabs.TabPane = TabPane;

export default Tabs;
